import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';

// Fix Leaflet z-index issues - constrain Leaflet to stay below sidebar (z-[1000])
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .leaflet-container {
      z-index: 1 !important;
    }
    .leaflet-control-container {
      z-index: 2 !important;
    }
    .leaflet-popup {
      z-index: 3 !important;
    }
    .leaflet-pane {
      z-index: 1 !important;
    }
    .leaflet-map-pane {
      z-index: 1 !important;
    }
    .leaflet-tile-pane {
      z-index: 1 !important;
    }
    .leaflet-overlay-pane {
      z-index: 2 !important;
    }
    .leaflet-shadow-pane {
      z-index: 2 !important;
    }
    .leaflet-marker-pane {
      z-index: 3 !important;
    }
    .leaflet-tooltip-pane {
      z-index: 4 !important;
    }
    .leaflet-popup-pane {
      z-index: 5 !important;
    }
  `;
  document.head.appendChild(style);
}

const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Truck icon for current location - using SVG path for better visibility
const TruckIcon = L.divIcon({
  className: 'truck-marker',
  html: `
    <div style="
      width: 32px; 
      height: 32px; 
      background: #EF4444; 
      border-radius: 50% 50% 50% 0; 
      transform: rotate(-45deg); 
      border: 3px solid white; 
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <span style="
        transform: rotate(45deg); 
        color: white; 
        font-size: 18px;
        font-weight: bold;
      ">🚛</span>
    </div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

// Workaround: some React-Leaflet TypeScript versions misreport prop types.
// Casting components to `any` avoids false-positive TS errors for props like `center` and `attribution`.
const AnyMapContainer = MapContainer as unknown as any;
const AnyTileLayer = TileLayer as unknown as any;

const LiveFollower: React.FC<{ position: [number, number] | null; isLive: boolean; trackerId: string | null }> = ({ position, isLive, trackerId }) => {
  const map = useMap();
  const [isFollowing, setIsFollowing] = useState(true);
  const isFollowingRef = useRef(true);
  const lastTrackerId = useRef<string | null>(null);

  // Sync state to ref
  useEffect(() => {
    isFollowingRef.current = isFollowing;
  }, [isFollowing]);

  // Handle User Interactions (Native DOM Events)
  useEffect(() => {
    const container = map.getContainer();

    const stopFollowing = (e: Event) => {
      // Ignore clicks on controls (zoom, layers, etc)
      if ((e.target as HTMLElement).closest('.leaflet-control')) return;

      // If we are currently following, stop it
      if (isFollowingRef.current) {
        console.log("Interaction detected: Stopping Live Follow");
        // Update Ref IMMEDIATELY to block any pending updates in the loop
        isFollowingRef.current = false;
        setIsFollowing(false);
      }
    };

    // Native listeners are immune to map.panTo() events
    container.addEventListener('mousedown', stopFollowing, { capture: true });
    container.addEventListener('wheel', stopFollowing, { capture: true });
    container.addEventListener('touchstart', stopFollowing, { capture: true });

    return () => {
      container.removeEventListener('mousedown', stopFollowing);
      container.removeEventListener('wheel', stopFollowing);
      container.removeEventListener('touchstart', stopFollowing);
    };
  }, [map]);

  // Handle Position Updates
  useEffect(() => {
    if (!position || !trackerId) return;

    // 1. Tracker Switch: Always force snap to new truck
    if (trackerId !== lastTrackerId.current) {
      lastTrackerId.current = trackerId;
      isFollowingRef.current = true; // Reset ref manually
      setIsFollowing(true);
      map.setView(position, 15);
      return;
    }

    // 2. Live Update: Only move if allowed
    if (isLive && isFollowingRef.current) {
      map.panTo(position, { animate: true, duration: 0.5 });
    }
  }, [position, isLive, map, trackerId]);

  if (isFollowing) return null;

  return (
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
        <div className="bg-white/90 backdrop-blur-sm border border-blue-500 text-blue-900 px-4 py-2 rounded-full shadow-lg text-sm font-semibold flex items-center gap-3 transition-all transform hover:scale-105">
        <span className="flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
        </span>
          <span>Live tracking paused</span>
          <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent this click from triggering the "stop" listener again
                isFollowingRef.current = true;
                setIsFollowing(true);
                if (position) map.panTo(position, { animate: true, duration: 0.5 });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full text-xs transition-colors shadow-sm"
          >
            Resume
          </button>
        </div>
      </div>
  );
};

const GPSManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isAdmin = user?.role === 'ADMIN';
  const [searchParams] = useSearchParams();

  type RoutePoint = { lat: number; lng: number; t: string; speed: number };
  type Tracker = {
    terminalId: string;
    trackerName?: string;
    name?: string;
    vehicleNumber?: string;
    driverName?: string;
    driverPhone?: string;
    status?: string;
    tenantId?: number | null;
    isOnline?: boolean;
  };

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    trackerName: '',
    name: '',
    vehicleNumber: '',
    driverName: '',
    driverPhone: '',
    status: 'active',
    tenantId: '',
  });
  const [admins, setAdmins] = useState<Array<{
    id: number;
    name: string;
    email: string;
    tenantId?: number | null;
  }>>([]);

  // Stops modal state
  const [showStopsModal, setShowStopsModal] = useState(false);

  // Stop management state
  const [showManageStopsModal, setShowManageStopsModal] = useState(false);
  const [storedStops, setStoredStops] = useState<Array<{
    _id: string;
    name: string;
    latitude: number;
    longitude: number;
    radius: number;
    trackerId: string;
  }>>([]);
  const [editingStop, setEditingStop] = useState<{
    _id?: string;
    name: string;
    latitude: string;
    longitude: string;
    radius: string;
  } | null>(null);

  // Statistics modal state
  const [showStatsModal, setShowStatsModal] = useState(false);

  const [allStoredIds, setAllStoredIds] = useState<string[]>([]);
  const [activeTrackerIds, setActiveTrackerIds] = useState<string[]>([]);
  const [accessibleTrackerIds, setAccessibleTrackerIds] = useState<string[]>([]);
  const [trackers, setTrackers] = useState<Tracker[]>([]);
  const accessibleTrackerIdsRef = useRef<string[]>([]);
  const isFetchingTrackersRef = useRef<boolean>(false);
  const urlTrackerIdRef = useRef<string | null>(null); // Track tracker ID from URL
  const [selectedTrackerId, setSelectedTrackerId] = useState<string | null>(null);
  const [pointsByTracker, setPointsByTracker] = useState<Record<string, RoutePoint[]>>({});
  const [isLoadingRoute, setIsLoadingRoute] = useState<boolean>(false);
  const [animatedPos, setAnimatedPos] = useState<[number, number] | null>(null);
  const [isLiveUpdate, setIsLiveUpdate] = useState<boolean>(false);
  const animRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<number | null>(null);
  const dateRangeRef = useRef<{ from: number; to: number }>({ from: 0, to: 0 });
  const lastValidDurationRef = useRef<number>(0); // Preserve last valid duration
  const initialHistoricalTimeRangeRef = useRef<{ from: number; to: number } | null>(null); // Store initial historical data time range
  // no polling-based latest fetch tracking needed

  // Reverse geocoding removed by request; we will show coordinates only without external calls

  // Date filter state
  type DateFilterType = 'today' | 'yesterday' | 'last7' | 'custom';
  const [filterType, setFilterType] = useState<DateFilterType>('today');
  const [customFrom, setCustomFrom] = useState<string>(''); // yyyy-mm-dd
  const [customTo, setCustomTo] = useState<string>('');     // yyyy-mm-dd

  const getActiveRange = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const startOfDay = (date: Date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const endOfDay = (date: Date) => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999);
      return d.getTime();
    };

    if (filterType === 'today') {
      const from = startOfDay(today);
      const to = endOfDay(today);
      console.log('Today range:', { from, to, fromDate: new Date(from), toDate: new Date(to) });
      return { from, to };
    }
    if (filterType === 'yesterday') {
      const y = new Date(today);
      y.setDate(today.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    if (filterType === 'last7') {
      const from = new Date(today);
      from.setDate(today.getDate() - 6);
      return { from: startOfDay(from), to: endOfDay(today) };
    }
    // custom
    const parseDate = (s: string, end = false) => {
      if (!s) return NaN;
      const parts = s.split('-').map(Number);
      if (parts.length !== 3) return NaN;
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      return end ? endOfDay(d) : startOfDay(d);
    };
    const fromTs = parseDate(customFrom);
    const toTs = parseDate(customTo, true);
    // fallback to today if invalid
    if (isNaN(fromTs) || isNaN(toTs)) {
      return { from: startOfDay(today), to: endOfDay(today) };
    }
    return { from: fromTs, to: toTs };
  }, [filterType, customFrom, customTo]);

  // Update ref whenever date range changes
  useEffect(() => {
    dateRangeRef.current = getActiveRange;
  }, [getActiveRange]);

  // Removed polling: rely solely on WebSocket for live updates

  const startPollingActiveTrackers = () => {
    if (pollRef.current) return;
    // Increase polling interval to reduce API calls (every 10 seconds instead of 5)
    const poll = window.setInterval(async () => {
      try {
        const trackers = await apiService.get(`/gps/trackers`);
        const active = Array.isArray(trackers)
            ? trackers.filter((t: any) => t?.isOnline).map((t: any) => String(t.terminalId))
            : [];
        setActiveTrackerIds(active);
        // Only update selection if no tracker in URL and current selection is not in accessible trackers
        if (!urlTrackerIdRef.current) {
          setSelectedTrackerId((prev) => {
            const accessible = accessibleTrackerIdsRef.current;
            if (prev && accessible.includes(prev)) {
              return prev; // Keep current selection if it's accessible
            }
            // Otherwise, select first accessible tracker
            return accessible.length > 0 ? accessible[0] : (active[0] || null);
          });
        }
      } catch (_e) { }
    }, 10000); // Increased from 5000 to 10000ms
    pollRef.current = poll as unknown as number;
  };

  const stopPollingActiveTrackers = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Fetch accessible trackers and extract their IDs (single source of truth)
  const fetchAccessibleTrackers = async () => {
    // Prevent concurrent calls
    if (isFetchingTrackersRef.current) {
      return;
    }

    isFetchingTrackersRef.current = true;
    try {
      const trackersResponse = await apiService.get(`/gps/trackers`);
      const trackersArray = Array.isArray(trackersResponse) ? trackersResponse : [];
      const ids = trackersArray.map((t: any) => String(t.terminalId));
      console.log('Accessible tracker IDs:', ids);
      setAccessibleTrackerIds(ids);
      accessibleTrackerIdsRef.current = ids;
      // Store full tracker objects
      setTrackers(trackersArray);
      // Also update allStoredIds from the same data (no need for separate API call)
      setAllStoredIds(ids);
      // Tracker selection is now handled by URL parameter
    } catch (error) {
      console.error('Failed to fetch accessible trackers:', error);
      setAccessibleTrackerIds([]);
      setAllStoredIds([]);
      setTrackers([]);
    } finally {
      isFetchingTrackersRef.current = false;
    }
  };

  // Fetch accessible trackers on mount
  useEffect(() => {
    fetchAccessibleTrackers();
  }, []);

  // Fetch admins for SuperAdmin
  useEffect(() => {
    const fetchAdmins = async () => {
      if (isSuperAdmin) {
        try {
          const response = await apiService.get('/admin/admins');
          setAdmins(response?.admins || []);
        } catch (error) {
          console.error('Error fetching admins:', error);
        }
      }
    };
    fetchAdmins();
  }, [isSuperAdmin]);

  // Helper function to calculate distance between two coordinates (Haversine formula)
  // Returns distance in meters
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371000; // Earth's radius in meters
    const toRad = (deg: number) => (deg * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Fetch stored stops for the selected tracker
  const fetchStoredStops = async (trackerId: string) => {
    if (!trackerId) {
      setStoredStops([]);
      return;
    }
    try {
      const stops = await apiService.get(`/gps/tracker/${trackerId}/stops`);
      setStoredStops(Array.isArray(stops) ? stops : []);
    } catch (error) {
      console.error('Error fetching stops:', error);
      setStoredStops([]);
    }
  };

  // Fetch stops when tracker changes (use URL parameter to avoid conflicts with WebSocket updates)
  useEffect(() => {
    const trackerParam = searchParams.get('tracker');
    if (trackerParam) {
      fetchStoredStops(trackerParam);
    } else {
      setStoredStops([]);
    }
  }, [searchParams]);

  // Read tracker from URL parameter and update when URL changes
  // This should always take precedence over WebSocket/polling updates
  useEffect(() => {
    const trackerParam = searchParams.get('tracker');
    urlTrackerIdRef.current = trackerParam;
    if (trackerParam) {
      setSelectedTrackerId(trackerParam);
    } else {
      // If no tracker in URL, clear selection (or redirect to first tracker)
      setSelectedTrackerId(null);
    }
  }, [searchParams]);

  useEffect(() => {
    // Only attempt to connect if GPS socket URL is configured
    const gpsSocketUrl = process.env.REACT_APP_GPS_SOCKET;
    if (!gpsSocketUrl) {
      console.warn('REACT_APP_GPS_SOCKET is not set. Skipping GPS WebSocket connection. Falling back to polling.');
      startPollingActiveTrackers();
      return;
    }

    const socket = new WebSocket(gpsSocketUrl);
    wsRef.current = socket;

    socket.onopen = () => {
      // Only fetch if we don't have trackers yet (avoid duplicate call on mount)
      if (accessibleTrackerIds.length === 0) {
        fetchAccessibleTrackers();
      }
      stopPollingActiveTrackers();
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        switch (payload.type) {
          case 'status': {
            const ids: string[] = payload.activeTrackers || [];
            setActiveTrackerIds(ids);
            // Only update selection if no tracker in URL and current selection is not in accessible trackers
            if (!urlTrackerIdRef.current) {
              setSelectedTrackerId((prev) => {
                const accessible = accessibleTrackerIdsRef.current;
                if (prev && accessible.includes(prev)) {
                  return prev; // Keep current selection if it's accessible
                }
                // Otherwise, select first accessible tracker
                return accessible.length > 0 ? accessible[0] : (ids[0] || null);
              });
            }
            break;
          }
          case 'login_update': {
            // A new tracker logged in, refresh accessible trackers list
            // Use debounce to avoid multiple rapid calls
            if (pollRef.current === null) {
              setTimeout(() => {
                fetchAccessibleTrackers();
              }, 1000);
            }
            break;
          }
          case 'logout_update': {
            const ids: string[] = payload.activeTrackers || [];
            setActiveTrackerIds(ids);
            // Only update selection if no tracker in URL and current selection is not in accessible trackers
            if (!urlTrackerIdRef.current) {
              setSelectedTrackerId((prev) => {
                const accessible = accessibleTrackerIdsRef.current;
                if (prev && accessible.includes(prev)) {
                  return prev; // Keep current selection if it's accessible
                }
                // Otherwise, select first accessible tracker
                return accessible.length > 0 ? accessible[0] : (ids[0] || null);
              });
            }
            break;
          }
          case 'decoded': {
            const terminalId: string = payload.terminalId;

            // Only process GPS data for the tracker currently in the URL
            const currentUrlTracker = urlTrackerIdRef.current || selectedTrackerId;
            if (currentUrlTracker && terminalId !== currentUrlTracker) {
              return; // Skip if this is not the tracker from URL
            }

            const d = payload.data || {};
            // Handle both lat/lng and latitude/longitude formats
            const lat = d.lat !== undefined ? d.lat : d.latitude;
            const lng = d.lng !== undefined ? d.lng : d.longitude;
            const hasGps = lat !== undefined && lng !== undefined && typeof lat === 'number' && typeof lng === 'number' && lat !== 0 && lng !== 0;
            if (!hasGps) return;

            console.log('Raw GPS data:', d);
            console.log('Speed value:', d.speed, 'Type:', typeof d.speed);

            // Parse timestamp - handle both ISO format and "YYYY-MM-DD HH:mm:ss" format
            let timestamp: string;
            if (d.timestamp) {
              // If timestamp is in "YYYY-MM-DD HH:mm:ss" format, convert it
              if (typeof d.timestamp === 'string' && d.timestamp.includes(' ') && !d.timestamp.includes('T')) {
                timestamp = new Date(d.timestamp.replace(' ', 'T')).toISOString();
              } else {
                timestamp = new Date(d.timestamp).toISOString();
              }
            } else {
              timestamp = new Date().toISOString();
            }

            const newPoint: RoutePoint = {
              lat: lat as number,
              lng: lng as number,
              t: timestamp,
              speed: typeof d.speed === 'number' ? d.speed : 0,
            };

            console.log('Processed GPS point:', newPoint);

            // Filter live points by active date range (using ref for latest value)
            const pointTime = new Date(newPoint.t).getTime();
            const range = dateRangeRef.current;
            if (isNaN(pointTime) || pointTime < range.from || pointTime > range.to) {
              return; // out of range, ignore
            }

            setPointsByTracker((prev) => {
              const existing = prev[terminalId] || [];
              // Check if this point already exists (avoid duplicates)
              const isDuplicate = existing.some(p =>
                  p.lat === newPoint.lat &&
                  p.lng === newPoint.lng &&
                  Math.abs(new Date(p.t).getTime() - pointTime) < 1000 // within 1 second
              );
              if (isDuplicate) {
                return prev; // Don't update if duplicate
              }
              const next = [...existing, newPoint].slice(-2000); // keep last N points per tracker
              // Mark as live update since this is for the current tracker
              setIsLiveUpdate(true);
              console.log('Live update for selected tracker:', terminalId, 'New point:', newPoint);
              // Reset flag after a short delay
              setTimeout(() => setIsLiveUpdate(false), 1000);
              return { ...prev, [terminalId]: next };
            });

            // Auto-select first tracker if none selected and no tracker in URL
            if (!urlTrackerIdRef.current && !selectedTrackerId) {
              setSelectedTrackerId(terminalId);
            }
            break;
          }
          default:
            break;
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    socket.onerror = (evt) => {
      console.error('GPS WebSocket error:', evt);
      startPollingActiveTrackers();
    };

    socket.onclose = () => {
      wsRef.current = null;
    };

    return () => {
      try {
        socket.close();
      } catch (_e) { }
      stopPollingActiveTrackers();
    };
  }, []);

  const routePoints: RoutePoint[] = useMemo(() => {
    if (!selectedTrackerId) return [];
    return pointsByTracker[selectedTrackerId] || [];
  }, [pointsByTracker, selectedTrackerId]);

  // Filter existing points when date range changes
  useEffect(() => {
    // Reset duration ref and initial historical range when date range or tracker changes
    lastValidDurationRef.current = 0;
    initialHistoricalTimeRangeRef.current = null;

    // Clear current tracker's points to avoid mixing ranges
    if (selectedTrackerId) {
      setIsLoadingRoute(true);
      setPointsByTracker((prev) => ({ ...prev, [selectedTrackerId]: [] }));
    }

    setPointsByTracker((prev) => {
      const filtered: Record<string, RoutePoint[]> = {};
      Object.keys(prev).forEach((trackerId) => {
        filtered[trackerId] = prev[trackerId].filter((p) => {
          const ts = new Date(p.t).getTime();
          return !isNaN(ts) && ts >= getActiveRange.from && ts <= getActiveRange.to;
        });
      });
      return filtered;
    });
  }, [getActiveRange.from, getActiveRange.to, selectedTrackerId]);

  // Fetch historical route when tracker selection or date filter changes
  useEffect(() => {
    const fetchHistoricalRoute = async (terminalId: string) => {
      try {
        setIsLoadingRoute(true);
        // Pass date range parameters to backend for efficient filtering
        const params = new URLSearchParams({
          from: String(getActiveRange.from),
          to: String(getActiveRange.to),
        });

        console.log('Fetching route:', {
          terminalId,
          from: getActiveRange.from,
          to: getActiveRange.to,
          fromDate: new Date(getActiveRange.from).toISOString(),
          toDate: new Date(getActiveRange.to).toISOString(),
          filterType
        });

        const response = await apiService.get(`/gps/route/${terminalId}?${params.toString()}`);
        console.log('Route response:', response?.length, 'points');

        // Transform the response data to match RoutePoint structure
        // Backend already filtered by date range, so no need to filter again
        const transformedPoints: RoutePoint[] = Array.isArray(response)
            ? response.map((point: any) => ({
              lat: point.lat || point.latitude || 0,
              lng: point.lng || point.longitude || 0,
              t: point.timestamp ? new Date(point.timestamp).toISOString() : new Date().toISOString(),
              speed: typeof point.speed === 'number' ? point.speed : 0,
            }))
            : [];

        // Store the initial historical data time range to cap duration calculation
        if (transformedPoints.length > 0) {
          const sortedPoints = [...transformedPoints].sort((a, b) => {
            const timeA = new Date(a.t).getTime();
            const timeB = new Date(b.t).getTime();
            return timeA - timeB;
          });
          const firstTime = new Date(sortedPoints[0].t).getTime();
          const lastTime = new Date(sortedPoints[sortedPoints.length - 1].t).getTime();
          if (!isNaN(firstTime) && !isNaN(lastTime)) {
            initialHistoricalTimeRangeRef.current = { from: firstTime, to: lastTime };
          }
        } else {
          initialHistoricalTimeRangeRef.current = null;
        }

        setPointsByTracker((prev) => ({ ...prev, [terminalId]: transformedPoints }));
        // Reset live update flag when loading historical data
        setIsLiveUpdate(false);
      } catch (_e) {
        // ignore errors
      }
      finally {
        setIsLoadingRoute(false);
      }
    };

    if (selectedTrackerId) {
      fetchHistoricalRoute(selectedTrackerId);
    }
  }, [selectedTrackerId, getActiveRange.from, getActiveRange.to]);

  type StopInfo = {
    indexStart: number;
    indexEnd: number;
    lat: number;
    lng: number;
    from: string;
    to: string;
    durationMin: number;
    stopName?: string; // Name from stored stop if matched
  };

  // Helper function to format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };

  const { stops, stopCount, distanceKm, durationMin } = useMemo(() => {
    console.log('Calculating stops for routePoints:', routePoints.length, 'points');
    console.log('Sample routePoints speeds:', routePoints.slice(0, 5).map(p => ({ speed: p.speed, lat: p.lat, lng: p.lng })));

    // Filter routePoints by active date range to ensure duration only reflects selected time period
    const range = getActiveRange;
    const filteredRoutePoints = routePoints.filter((p) => {
      const ts = new Date(p.t).getTime();
      return !isNaN(ts) && ts >= range.from && ts <= range.to;
    });

    // Compute contiguous stop clusters where speed <= 1
    const computedStops: StopInfo[] = [];
    let i = 0;
    while (i < filteredRoutePoints.length) {
      if (filteredRoutePoints[i].speed <= 1) {
        const start = i;
        let end = i;
        while (end + 1 < filteredRoutePoints.length && filteredRoutePoints[end + 1].speed <= 1) {
          end += 1;
        }
        const from = filteredRoutePoints[start].t;
        const to = filteredRoutePoints[end].t;
        const fromTime = new Date(from).getTime();
        const toTime = new Date(to).getTime();
        const durationMin = isNaN(fromTime) || isNaN(toTime) ? 0 : Math.max(0, (toTime - fromTime) / 60000);

        // Only add stop if duration is greater than 1 minute
        if (durationMin > 1) {
          const mid = Math.floor((start + end) / 2);
          const stopLat = filteredRoutePoints[mid].lat;
          const stopLng = filteredRoutePoints[mid].lng;

          // Match with stored stops
          let matchedStopName: string | undefined;
          for (const storedStop of storedStops) {
            const distance = calculateDistance(stopLat, stopLng, storedStop.latitude, storedStop.longitude);
            if (distance <= storedStop.radius) {
              matchedStopName = storedStop.name;
              break; // Use first match
            }
          }

          computedStops.push({
            indexStart: start,
            indexEnd: end,
            lat: stopLat,
            lng: stopLng,
            from,
            to,
            durationMin: Math.round(durationMin),
            stopName: matchedStopName,
          });
        }
        i = end + 1;
      } else {
        i += 1;
      }
    }

    // Haversine distance (using filtered points)
    const toRad = (v: number) => (v * Math.PI) / 180;
    let dist = 0;
    for (let j = 1; j < filteredRoutePoints.length; j += 1) {
      const a = filteredRoutePoints[j - 1];
      const b = filteredRoutePoints[j];
      const R = 6371; // km
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
      dist += R * c;
    }

    // Calculate duration from first to last point within the date range
    // Sort filtered points by timestamp to ensure correct calculation even if points arrive out of order
    const sortedPoints = [...filteredRoutePoints].sort((a, b) => {
      const timeA = new Date(a.t).getTime();
      const timeB = new Date(b.t).getTime();
      return timeA - timeB;
    });

    let totalMin = sortedPoints.length > 1
        ? (() => {
          let startTime = new Date(sortedPoints[0].t).getTime();
          let endTime = new Date(sortedPoints[sortedPoints.length - 1].t).getTime();

          if (isNaN(startTime) || isNaN(endTime)) {
            // If timestamps are invalid, preserve last valid duration
            return lastValidDurationRef.current;
          }

          // If we have an initial historical time range, cap the duration to that range
          // This prevents live data from extending the duration beyond the initial historical data
          const initialRange = initialHistoricalTimeRangeRef.current;
          if (initialRange) {
            // Only use points within the initial historical range for duration calculation
            const pointsInInitialRange = sortedPoints.filter(p => {
              const pt = new Date(p.t).getTime();
              return pt >= initialRange.from && pt <= initialRange.to;
            });

            if (pointsInInitialRange.length > 1) {
              const initialStartTime = new Date(pointsInInitialRange[0].t).getTime();
              const initialEndTime = new Date(pointsInInitialRange[pointsInInitialRange.length - 1].t).getTime();
              if (!isNaN(initialStartTime) && !isNaN(initialEndTime)) {
                startTime = initialStartTime;
                endTime = initialEndTime;
              }
            }
          }

          const durationMs = endTime - startTime;
          if (durationMs <= 0) {
            // If duration is invalid, preserve last valid duration
            return lastValidDurationRef.current;
          }
          const calculatedDuration = Math.max(0, Math.round(durationMs / 60000));
          // Update ref with valid duration
          lastValidDurationRef.current = calculatedDuration;
          return calculatedDuration;
        })()
        : (sortedPoints.length === 0 ? lastValidDurationRef.current : 0);

    // Update ref if we got a valid duration
    if (totalMin > 0) {
      lastValidDurationRef.current = totalMin;
    }

    console.log('Stop calculation results:', {
      stops: computedStops.length,
      distanceKm: dist,
      durationMin: totalMin,
      firstStop: computedStops[0],
      allStopsWithDuration: computedStops.map(s => ({ duration: s.durationMin, lat: s.lat, lng: s.lng }))
    });

    return { stops: computedStops, stopCount: computedStops.length, distanceKm: dist, durationMin: totalMin };
  }, [routePoints, storedStops, getActiveRange]);

  // Total time spent in stops (minutes)
  const totalStopMinutes = useMemo(() => {
    return stops.reduce((acc, s) => acc + (typeof s.durationMin === 'number' ? s.durationMin : 0), 0);
  }, [stops]);

  // Reverse geocoding disabled; nothing to fetch for stops

  const polylinePositions = useMemo(() => routePoints.map(p => [p.lat, p.lng]) as [number, number][], [routePoints]);
  const start = routePoints[0];
  const end = routePoints[routePoints.length - 1];

  // Smoothly animate marker between last two points (runs after routePoints declared)
  useEffect(() => {
    if (!routePoints || routePoints.length === 0) return;
    if (routePoints.length === 1) {
      setAnimatedPos([routePoints[0].lat, routePoints[0].lng]);
      return;
    }
    const a = routePoints[routePoints.length - 2];
    const b = routePoints[routePoints.length - 1];
    const startPos: [number, number] = [a.lat, a.lng];
    const endPos: [number, number] = [b.lat, b.lng];
    const durationMs = 600; // smooth 0.6s
    const t0 = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs);
      const lat = startPos[0] + (endPos[0] - startPos[0]) * p;
      const lng = startPos[1] + (endPos[1] - startPos[1]) * p;
      setAnimatedPos([lat, lng]);
      if (p < 1) {
        animRef.current = requestAnimationFrame(step);
      }
    };
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [routePoints]);

  // Combine all known IDs but filter to only show accessible trackers
  const allKnownIds = useMemo(() => {
    // Use accessibleTrackerIds as the source of truth for what the user can see
    const combined = new Set([...accessibleTrackerIds, ...activeTrackerIds]);
    // Filter to only include IDs that are in accessibleTrackerIds (user has access)
    const filtered = Array.from(combined).filter(id =>
        accessibleTrackerIds.length === 0 || accessibleTrackerIds.includes(id)
    );
    return filtered.sort();
  }, [accessibleTrackerIds, activeTrackerIds]);

  // Tracker is selected from URL parameter, no auto-select needed

  // Get status dot for tracker (online/offline)
  const getStatusDot = (terminalId: string) => {
    const isOnline = activeTrackerIds.includes(terminalId);
    return (
        <span
            className={`inline-block w-2 h-2 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
            title={isOnline ? 'Online' : 'Offline'}
        />
    );
  };

  // Get tracker display name
  const getTrackerDisplayName = (terminalId: string | null): string => {
    if (!terminalId) return '';
    const tracker = trackers.find((t: Tracker) => t.terminalId === terminalId);
    return tracker ? (tracker.trackerName || tracker.name || tracker.terminalId) : terminalId;
  };

  // Handle edit button click
  const handleEditClick = () => {
    if (!selectedTrackerId) {
      toast.error('No tracker selected');
      return;
    }
    const tracker = trackers.find((t: Tracker) => t.terminalId === selectedTrackerId);
    if (tracker) {
      setEditFormData({
        trackerName: tracker.trackerName || '',
        name: tracker.name || '',
        vehicleNumber: tracker.vehicleNumber || '',
        driverName: tracker.driverName || '',
        driverPhone: tracker.driverPhone || '',
        status: tracker.status || 'active',
        tenantId: tracker.tenantId?.toString() || '',
      });
      setShowEditModal(true);
    } else {
      toast.error('Tracker not found');
    }
  };

  // Handle edit form submit
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTrackerId) return;

    try {
      const updateData: any = {};

      // Admins can update trackerName, vehicleNumber, driverName, and driverPhone
      if (isAdmin) {
        updateData.trackerName = editFormData.trackerName;
        updateData.vehicleNumber = editFormData.vehicleNumber;
        updateData.driverName = editFormData.driverName;
        updateData.driverPhone = editFormData.driverPhone;
      } else {
        // Non-admin users (SuperAdmin) can update all fields
        updateData.trackerName = editFormData.trackerName;
        updateData.status = editFormData.status;
        updateData.name = editFormData.name;
        updateData.vehicleNumber = editFormData.vehicleNumber;
        updateData.driverName = editFormData.driverName;
        updateData.driverPhone = editFormData.driverPhone;
        if (isSuperAdmin && editFormData.tenantId !== undefined) {
          updateData.tenantId = editFormData.tenantId ? parseInt(editFormData.tenantId) : null;
        }
      }

      await apiService.put(`/gps/tracker/${selectedTrackerId}`, updateData);
      toast.success('Tracker updated successfully');
      setShowEditModal(false);
      // Refresh tracker list
      fetchAccessibleTrackers();
    } catch (error: any) {
      console.error('Error updating tracker:', error);
      toast.error(error.response?.data?.error || 'Failed to update tracker');
    }
  };

  return (
      <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
        {/* Top Filters removed; replaced with compact overlay on the map */}

        <div className={'flex gap-3 justify-between mb-4 items-center flex-wrap'}>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">GPS Tracker Management</h2>
            {selectedTrackerId && (
                <div className="text-sm text-gray-600 mt-1">
                  Tracker: {getTrackerDisplayName(selectedTrackerId)}
                  {activeTrackerIds.includes(selectedTrackerId) ? (
                      <span className="ml-2 text-green-600">🟢 Online</span>
                  ) : (
                      <span className="ml-2 text-red-600">🔴 Offline</span>
                  )}
                </div>
            )}
          </div>
          <div className={'flex gap-3 justify-end'}>
            {/* <button type="button"
                  className="text-white flex gap-2 rounded-xl items-center bg-green-600 hover:bg-green-800 box-border border border-transparent hover:bg-success-strong focus:ring-4 focus:ring-success-medium shadow-xs font-medium leading-5 rounded-base text-sm px-4 py-2.5 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 className="lucide lucide-eye h-4 w-4">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            View
          </button> */}
            <button
                type="button"
                onClick={handleEditClick}
                disabled={!selectedTrackerId}
                className="text-white flex gap-2 rounded-xl items-center bg-blue-600 hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed box-border border border-transparent hover:bg-success-strong focus:ring-4 focus:ring-success-medium shadow-xs font-medium leading-5 rounded-base text-sm px-2 md:px-4 py-2.5 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   className="lucide lucide-pen-square h-4 w-4">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"></path>
              </svg>
              <span className="hidden md:inline">Edit</span>
            </button>
            <button
                type="button"
                onClick={() => setShowStopsModal(true)}
                disabled={!selectedTrackerId || stopCount === 0}
                className="text-white flex gap-2 rounded-xl items-center bg-amber-600 hover:bg-amber-800 disabled:bg-gray-400 disabled:cursor-not-allowed box-border border border-transparent focus:ring-4 focus:ring-amber-medium shadow-xs font-medium leading-5 rounded-base text-sm px-2 md:px-4 py-2.5 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   className="lucide lucide-map-pin h-4 w-4">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span className="hidden md:inline">Stops ({stopCount})</span>
              <span className="md:hidden">{stopCount}</span>
            </button>
            <button
                type="button"
                onClick={() => setShowManageStopsModal(true)}
                disabled={!selectedTrackerId}
                className="text-white flex gap-2 rounded-xl items-center bg-purple-600 hover:bg-purple-800 disabled:bg-gray-400 disabled:cursor-not-allowed box-border border border-transparent focus:ring-4 focus:ring-purple-medium shadow-xs font-medium leading-5 rounded-base text-sm px-2 md:px-4 py-2.5 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   className="lucide lucide-settings h-4 w-4">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <span className="hidden md:inline">Manage Stops</span>
            </button>
            <button
                type="button"
                onClick={() => setShowStatsModal(true)}
                className="md:hidden text-white flex gap-2 rounded-xl items-center bg-green-600 hover:bg-green-800 box-border border border-transparent focus:ring-4 focus:ring-green-medium shadow-xs font-medium leading-5 rounded-base text-sm px-2 py-2.5 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   className="lucide lucide-bar-chart-3 h-4 w-4">
                <path d="M3 3v18h18"></path>
                <path d="M18 17V9"></path>
                <path d="M13 17V5"></path>
                <path d="M8 17v-3"></path>
              </svg>
            </button>
          </div>
        </div>


        {/* GPS Map & Route Details */}
        {/* Summary Section - Desktop Only */}
        <div className="hidden md:block bg-white rounded-lg shadow-lg mb-4 md:mb-6">
          {/* Statistics and Date Range Section - Single Line */}
          <div className="p-4 md:p-6">
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              {/* Date Range - Compact */}
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <select
                    className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as any)}
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7">Last 7 Days</option>
                  <option value="custom">Custom</option>
                </select>
                {filterType === 'custom' && (
                    <>
                      <input
                          type="date"
                          className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={customFrom}
                          onChange={(e) => setCustomFrom(e.target.value)}
                      />
                      <span className="text-xs text-gray-500">to</span>
                      <input
                          type="date"
                          className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={customTo}
                          onChange={(e) => setCustomTo(e.target.value)}
                      />
                    </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-blue-700 font-medium">Distance:</span>
                <span className="text-lg font-bold text-blue-900">{distanceKm.toFixed(1)} km</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Duration:</span>
                <span className="text-lg font-bold text-emerald-900">{formatDuration(durationMin)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-amber-700 font-medium">Stops:</span>
                <span className="text-lg font-bold text-amber-900">{stopCount}</span>
                {stopCount > 0 && (
                    <span className="text-xs text-gray-500">({formatDuration(totalStopMinutes)} total)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-purple-700 font-medium">Speed:</span>
                <span className="text-lg font-bold text-purple-900">
                    {routePoints.length > 0 ? routePoints[routePoints.length - 1]?.speed?.toFixed(1) || '0' : '0'} km/h
                  </span>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section - Full Width */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="relative h-[calc(100vh-20rem)] md:h-[calc(100vh-16rem)] min-h-[32rem]" style={{ isolation: 'isolate', zIndex: 1 }}>
            {/* Loading overlay on map */}
            {isLoadingRoute && (
                <div
                    className="absolute inset-0 z-20 bg-white/60 backdrop-blur-sm flex items-center justify-center">
                  <div className="flex items-center gap-3 text-gray-700">
                    <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                              strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span className="text-sm font-medium">Loading route…</span>
                  </div>
                </div>
            )}

            <AnyMapContainer
                center={[20.0, 77.0]} // Static default center
                zoom={5}
                className="h-full w-full"
                style={{ zIndex: 1 }}>
              <AnyTileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LiveFollower
                  position={animatedPos || (end ? [end.lat, end.lng] : null)}
                  isLive={!!animatedPos || isLiveUpdate}
                  trackerId={selectedTrackerId}
              />
              {polylinePositions.length > 1 && (
                  <Polyline
                      key={`polyline-${selectedTrackerId}-${polylinePositions.length}`}
                      positions={polylinePositions}
                      pathOptions={{ color: '#0060b9', weight: 4 }}
                  />
              )}

              {/* Start Marker */}
              {start && (
                  <Marker position={[start.lat, start.lng]}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">Start</div>
                        <div>{new Date(start.t).toLocaleString()}</div>
                      </div>
                    </Popup>
                  </Marker>
              )}

              {/* Live Truck Marker (animated) */}
              {end && (
                  <Marker
                      key={`truck-${end.lat}-${end.lng}-${end.t}`}
                      position={animatedPos || [end.lat, end.lng]}
                      icon={TruckIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">Current Location (Truck)</div>
                        <div>{new Date(end.t).toLocaleString()}</div>
                        <div>Speed: {routePoints[routePoints.length - 1]?.speed?.toFixed(1) || '0'} km/h</div>
                      </div>
                    </Popup>
                  </Marker>
              )}

              {/* Stop Markers */}
              {stops.map((s, idx) => {
                return (
                    <Marker key={`stop-${idx}`} position={[s.lat, s.lng]}>
                      <Popup>
                        <div className="text-sm">
                          <div className="font-semibold text-red-600">
                            {s.stopName || `Stop #${idx + 1}`}
                          </div>
                          <div className="font-medium text-gray-700 mb-1">📍 {s.lat.toFixed(4)}, {s.lng.toFixed(4)}</div>

                          <div className="border-t border-gray-200 pt-1 mt-2 space-y-0.5">
                            <div className="text-xs">
                              <span className="text-gray-500">From:</span>{' '}
                              {(() => {
                                const fromDate = new Date(s.from);
                                return isNaN(fromDate.getTime()) ? 'Invalid Date' : fromDate.toLocaleTimeString();
                              })()}
                            </div>
                            <div className="text-xs">
                              <span className="text-gray-500">To:</span>{' '}
                              {(() => {
                                const toDate = new Date(s.to);
                                return isNaN(toDate.getTime()) ? 'Invalid Date' : toDate.toLocaleTimeString();
                              })()}
                            </div>
                            <div className="text-xs">
                              <span className="text-gray-500">Duration:</span> {formatDuration(s.durationMin)}
                            </div>
                          </div>
                          {!s.stopName && (
                              <div className="border-t border-gray-200 pt-2 mt-2">
                                <button
                                    onClick={() => {
                                      setEditingStop({
                                        name: '',
                                        latitude: s.lat.toString(),
                                        longitude: s.lng.toString(),
                                        radius: '500',
                                      });
                                      setShowManageStopsModal(true);
                                    }}
                                    className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                  Save as Named Stop
                                </button>
                              </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                );
              })}

              {/* Moving point markers removed as per requirement: only show stops (> 1 min) */}
            </AnyMapContainer>
          </div>
        </div>

        {/* Edit Tracker Modal */}
        {showEditModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ zIndex: 10000 }}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Tracker - {selectedTrackerId}
                  </h3>
                  <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <form onSubmit={handleEditSubmit} className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tracker Name</label>
                      <input
                          type="text"
                          value={editFormData.trackerName}
                          onChange={(e) => setEditFormData({ ...editFormData, trackerName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    {isSuperAdmin && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                            <input
                                type="text"
                                value={editFormData.name}
                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                value={editFormData.status}
                                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="maintenance">Maintenance</option>
                            </select>
                          </div>
                        </>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Number</label>
                      <input
                          type="text"
                          value={editFormData.vehicleNumber}
                          onChange={(e) => setEditFormData({ ...editFormData, vehicleNumber: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Driver Name</label>
                      <input
                          type="text"
                          value={editFormData.driverName}
                          onChange={(e) => setEditFormData({ ...editFormData, driverName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Driver Phone</label>
                      <input
                          type="text"
                          value={editFormData.driverPhone}
                          onChange={(e) => setEditFormData({ ...editFormData, driverPhone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    {isSuperAdmin && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Admin</label>
                          <select
                              value={editFormData.tenantId}
                              onChange={(e) => setEditFormData({ ...editFormData, tenantId: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="">No Tenant</option>
                            {admins
                                .filter(admin => admin.tenantId !== null && admin.tenantId !== undefined)
                                .map(admin => (
                                    <option key={admin.id} value={admin.tenantId?.toString()}>
                                      {admin.name} ({admin.email}) - Tenant ID: {admin.tenantId}
                                    </option>
                                ))}
                          </select>
                        </div>
                    )}
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Stops Modal */}
        {showStopsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ zIndex: 10000 }}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Stops ({stopCount})
                  </h3>
                  <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-gray-700 border border-gray-200">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Total stop time: <span className="text-red-600 font-semibold">{formatDuration(totalStopMinutes)}</span>
                </span>
                    <button onClick={() => setShowStopsModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {isLoadingRoute && (
                      <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
                        <svg className="animate-spin h-4 w-4 text-blue-600" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Loading stops…
                      </div>
                  )}
                  {stops.length === 0 && (
                      <div className="text-sm text-gray-500 text-center py-8">No stops in this time range.</div>
                  )}
                  <div className="space-y-3">
                    {stops.map((s, idx) => {
                      return (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-gray-300 transition-all">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-semibold text-gray-900">{s.stopName || `Stop #${idx + 1}`}</div>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                          {formatDuration(s.durationMin)}
                        </span>
                            </div>
                            <div className="space-y-1.5">
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">Location:</span> {s.lat.toFixed(4)}, {s.lng.toFixed(4)}
                              </div>
                              <div className="text-sm text-gray-500">
                                <span className="font-medium">Time:</span>{' '}
                                {(() => {
                                  const fromDate = new Date(s.from);
                                  const toDate = new Date(s.to);
                                  const fromStr = isNaN(fromDate.getTime()) ? 'Invalid Date' : fromDate.toLocaleString();
                                  const toStr = isNaN(toDate.getTime()) ? 'Invalid Date' : toDate.toLocaleString();
                                  return `${fromStr} - ${toStr}`;
                                })()}
                              </div>
                              {!s.stopName && (
                                  <div className="pt-2">
                                    <button
                                        onClick={() => {
                                          setShowStopsModal(false);
                                          setEditingStop({
                                            name: '',
                                            latitude: s.lat.toString(),
                                            longitude: s.lng.toString(),
                                            radius: '500',
                                          });
                                          setShowManageStopsModal(true);
                                        }}
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                    >
                                      Save as Named Stop
                                    </button>
                                  </div>
                              )}
                            </div>
                          </div>
                      );
                    })}
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                      onClick={() => setShowStopsModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Statistics Modal */}
        {showStatsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ zIndex: 10000 }}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Route Statistics
                  </h3>
                  <button onClick={() => setShowStatsModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4 md:p-6">
                  <div className="flex flex-wrap items-center gap-4 md:gap-6">
                    {/* Date Range - Compact */}
                    <div className="flex items-center gap-2 w-full md:w-auto">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <select
                          className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value as any)}
                      >
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="last7">Last 7 Days</option>
                        <option value="custom">Custom</option>
                      </select>
                      {filterType === 'custom' && (
                          <>
                            <input
                                type="date"
                                className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                            />
                            <span className="text-xs text-gray-500">to</span>
                            <input
                                type="date"
                                className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                            />
                          </>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-blue-700 font-medium">Distance:</span>
                      <span className="text-lg font-bold text-blue-900">{distanceKm.toFixed(1)} km</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Duration:</span>
                      <span className="text-lg font-bold text-emerald-900">{formatDuration(durationMin)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-amber-700 font-medium">Stops:</span>
                      <span className="text-lg font-bold text-amber-900">{stopCount}</span>
                      {stopCount > 0 && (
                          <span className="text-xs text-gray-500">({formatDuration(totalStopMinutes)} total)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-purple-700 font-medium">Speed:</span>
                      <span className="text-lg font-bold text-purple-900">
                          {routePoints.length > 0 ? routePoints[routePoints.length - 1]?.speed?.toFixed(1) || '0' : '0'} km/h
                        </span>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                      onClick={() => setShowStatsModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Manage Stops Modal */}
        {showManageStopsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ zIndex: 10000 }}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Manage Stops - {getTrackerDisplayName(selectedTrackerId)}
                  </h3>
                  <button onClick={() => {
                    setShowManageStopsModal(false);
                    setEditingStop(null);
                  }} className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6">
                  {/* Form to create/edit stop */}
                  {editingStop && (
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-semibold text-gray-900">
                            {editingStop._id ? 'Edit Stop' : 'Create New Stop'}
                          </h4>
                        </div>
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          if (!selectedTrackerId) return;

                          const currentStop = editingStop || {
                            name: '',
                            latitude: '',
                            longitude: '',
                            radius: '500',
                          };

                          if (!currentStop.name || !currentStop.latitude || !currentStop.longitude) {
                            toast.error('Please fill in all required fields');
                            return;
                          }

                          try {
                            const stopData = {
                              name: currentStop.name,
                              latitude: parseFloat(currentStop.latitude),
                              longitude: parseFloat(currentStop.longitude),
                              radius: currentStop.radius ? parseFloat(currentStop.radius) : 500,
                            };

                            if (currentStop._id) {
                              // Update existing stop
                              await apiService.put(`/gps/tracker/${selectedTrackerId}/stops/${currentStop._id}`, stopData);
                              toast.success('Stop updated successfully');
                            } else {
                              // Create new stop
                              await apiService.post(`/gps/tracker/${selectedTrackerId}/stops`, stopData);
                              toast.success('Stop created successfully');
                            }

                            setEditingStop(null);
                            fetchStoredStops(selectedTrackerId);
                          } catch (error: any) {
                            console.error('Error saving stop:', error);
                            toast.error(error.response?.data?.error || 'Failed to save stop');
                          }
                        }}>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Stop Name *</label>
                              <input
                                  type="text"
                                  value={editingStop?.name || ''}
                                  onChange={(e) => setEditingStop({
                                    _id: editingStop?._id,
                                    name: e.target.value,
                                    latitude: editingStop?.latitude || '',
                                    longitude: editingStop?.longitude || '',
                                    radius: editingStop?.radius || '500',
                                  })}
                                  required
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="e.g., Warehouse, Customer Site"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Radius (meters)</label>
                              <input
                                  type="number"
                                  value={editingStop?.radius || '500'}
                                  onChange={(e) => setEditingStop({
                                    _id: editingStop?._id,
                                    name: editingStop?.name || '',
                                    latitude: editingStop?.latitude || '',
                                    longitude: editingStop?.longitude || '',
                                    radius: e.target.value,
                                  })}
                                  min="0"
                                  step="10"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Latitude *</label>
                              <input
                                  type="number"
                                  value={editingStop?.latitude || ''}
                                  onChange={(e) => setEditingStop({
                                    _id: editingStop?._id,
                                    name: editingStop?.name || '',
                                    latitude: e.target.value,
                                    longitude: editingStop?.longitude || '',
                                    radius: editingStop?.radius || '500',
                                  })}
                                  required
                                  step="any"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="e.g., 20.1234"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Longitude *</label>
                              <input
                                  type="number"
                                  value={editingStop?.longitude || ''}
                                  onChange={(e) => setEditingStop({
                                    _id: editingStop?._id,
                                    name: editingStop?.name || '',
                                    latitude: editingStop?.latitude || '',
                                    longitude: e.target.value,
                                    radius: editingStop?.radius || '500',
                                  })}
                                  required
                                  step="any"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="e.g., 77.5678"
                              />
                            </div>
                          </div>
                          <div className="mt-4 flex gap-2">
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                              {editingStop?._id ? 'Update' : 'Create'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingStop({
                                  name: '',
                                  latitude: '',
                                  longitude: '',
                                  radius: '500',
                                })}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                              {editingStop?._id ? 'Cancel' : 'Clear'}
                            </button>
                          </div>
                        </form>
                      </div>
                  )}

                  {/* {!editingStop && (
                <div className="mb-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingStop({
                      name: '',
                      latitude: '',
                      longitude: '',
                      radius: '500',
                    })}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    + Create New Stop
                  </button>
                </div>
              )} */}

                  {/* List of existing stops */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Existing Stops ({storedStops.length})</h4>
                    {storedStops.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-8">No stops created yet. Create one above.</div>
                    ) : (
                        <div className="space-y-3">
                          {storedStops.map((stop) => (
                              <div key={stop._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-all">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-900">{stop.name}</div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      📍 {stop.latitude.toFixed(6)}, {stop.longitude.toFixed(6)} • Radius: {stop.radius}m
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                        onClick={() => setEditingStop({
                                          _id: stop._id,
                                          name: stop.name,
                                          latitude: stop.latitude.toString(),
                                          longitude: stop.longitude.toString(),
                                          radius: stop.radius.toString(),
                                        })}
                                        className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                                    >
                                      Edit
                                    </button>
                                    <button
                                        onClick={async () => {
                                          if (!window.confirm(`Are you sure you want to delete "${stop.name}"?`)) return;
                                          if (!selectedTrackerId) return;

                                          try {
                                            await apiService.delete(`/gps/tracker/${selectedTrackerId}/stops/${stop._id}`);
                                            toast.success('Stop deleted successfully');
                                            fetchStoredStops(selectedTrackerId);
                                          } catch (error: any) {
                                            console.error('Error deleting stop:', error);
                                            toast.error(error.response?.data?.error || 'Failed to delete stop');
                                          }
                                        }}
                                        className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                          ))}
                        </div>
                    )}
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                      onClick={() => {
                        setShowManageStopsModal(false);
                        setEditingStop(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
        )}

      </div>
  );
};

export default GPSManagement;
